/**
 * Création / modification d'une solution du catalogue (administrateur).
 *
 * À gauche, le discours de la solution : ce que c'est, quand elle est
 * pertinente, la promesse, les arguments, les preuves. C'est ce qui alimente
 * les variables {{nous.*}} des modèles et le bloc « contexte du vendeur »
 * joint à chaque génération. À droite, les activités visées : c'est elles qui
 * décident à quelles entreprises la solution est proposée.
 *
 * L'assistant IA (« Décrire avec l'IA ») part d'une phrase et propose tous
 * ces champs, activités comprises. Il propose, il n'enregistre rien : la
 * proposition remplit le formulaire, où elle se relit et se corrige.
 */
import { useState } from "react";
import { toast } from "sonner";
import { X, Plus, Save, RefreshCw, Wand2, Check } from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import Modal from "@/crm/components/Modal";
import ActivityPicker from "@/crm/components/solutions/ActivityPicker";

const FIELD = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-indigo-500";
const LABEL = "block text-[12.5px] font-medium text-slate-600";
const HINT = "mt-1 block text-[11.5px] text-slate-400";

const EMPTY = {
  nom: "", type: "solution", description: "", contexte: "", proposition_valeur: "",
  differenciateurs: "", cible: "", prix: "", arguments: [], preuves: [],
  naf_prefixes: [], domaines: [], active: true, rang: 0,
};

export default function SolutionForm({ solution, onClose, onSaved }) {
  const isNew = !solution?.id;
  const [f, setF] = useState({ ...EMPTY, ...(solution || {}) });
  const [saving, setSaving] = useState(false);
  const [assistOpen, setAssistOpen] = useState(isNew);
  const set = (patch) => setF((s) => ({ ...s, ...patch }));

  /** Une proposition ne remplace que ce qu'elle apporte : un champ vide dans
   *  la réponse laisse intacte la saisie de l'administrateur. */
  const applyProposal = (proposal) => {
    const patch = {};
    for (const [key, value] of Object.entries(proposal)) {
      if (!(key in EMPTY) || key === "active" || key === "rang") continue;
      if (Array.isArray(value) ? value.length > 0 : Boolean(value)) patch[key] = value;
    }
    set(patch);
    setAssistOpen(false);
    toast.success("Proposition appliquée — relisez avant d'enregistrer");
  };

  const setItem = (field, i, v) => set({ [field]: f[field].map((x, j) => (j === i ? v : x)) });
  const addItem = (field) => set({ [field]: [...f[field], ""] });
  const removeItem = (field, i) => set({ [field]: f[field].filter((_, j) => j !== i) });

  const save = async () => {
    if (!f.nom.trim()) return toast.error("Le nom de la solution est requis");
    setSaving(true);
    const payload = {
      ...f,
      arguments: f.arguments.filter((a) => a.trim()),
      preuves: f.preuves.filter((p) => p.trim()),
      rang: Number(f.rang) || 0,
    };
    try {
      if (isNew) await api.post("/solutions", payload);
      else await api.put(`/solutions/${solution.id}`, payload);
      toast.success(isNew ? "Solution ajoutée au catalogue" : "Solution enregistrée");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Modal onClose={onClose} size="5xl" testid="solution-form"
      label={isNew ? "Nouvelle solution" : "Modifier la solution"}>
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-heading text-[17px] font-bold text-slate-900">
          {isNew ? "Nouvelle solution" : "Modifier la solution"}
        </h3>
        <div className="flex items-center gap-2">
          <button type="button" data-testid="solution-assist-btn" onClick={() => setAssistOpen((v) => !v)}
            aria-expanded={assistOpen}
            className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12.5px] font-medium ${
              assistOpen ? "border-indigo-200 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600 hover:border-indigo-200 hover:text-indigo-700"}`}>
            <Wand2 size={14} /> Décrire avec l'IA
          </button>
          <button onClick={onClose} aria-label="Fermer" className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
        </div>
      </div>

      {assistOpen && <AssistPanel current={f} onApply={applyProposal} onClose={() => setAssistOpen(false)} />}

      <div className="mt-4 grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-3">
          <div className="grid grid-cols-[minmax(0,1fr)_150px_96px] gap-2">
            <label className={LABEL}>Nom
              <input data-testid="solution-nom" value={f.nom} onChange={(e) => set({ nom: e.target.value })}
                placeholder="Ex. Caisse connectée" className={`${FIELD} mt-1`} />
            </label>
            <label className={LABEL}>Type
              <select data-testid="solution-type" value={f.type} onChange={(e) => set({ type: e.target.value })}
                className={`${FIELD} mt-1 bg-white`}>
                <option value="solution">Solution</option>
                <option value="produit">Produit</option>
              </select>
            </label>
            <label className={LABEL}>Ordre
              <input type="number" min={0} max={999} data-testid="solution-rang" value={f.rang}
                onChange={(e) => set({ rang: e.target.value })} className={`${FIELD} mt-1 font-mono`} />
            </label>
          </div>

          <label className={LABEL}>Ce que c'est
            <textarea rows={2} value={f.description} onChange={(e) => set({ description: e.target.value })}
              placeholder="Logiciel de caisse tactile relié au stock et à la comptabilité" className={`${FIELD} mt-1 resize-y`} />
          </label>
          <label className={LABEL}>Contexte d'usage — quand la proposer
            <textarea data-testid="solution-contexte" rows={2} value={f.contexte} onChange={(e) => set({ contexte: e.target.value })}
              placeholder="Restaurants qui encaissent encore à la main ou avec une caisse vieillissante, qui perdent du temps à la clôture"
              className={`${FIELD} mt-1 resize-y`} />
            <span className={HINT}>Le discours et le copilote s'en servent pour relier la solution à la situation du prospect.</span>
          </label>
          <label className={LABEL}>Promesse en une phrase
            <input data-testid="solution-promesse" value={f.proposition_valeur} onChange={(e) => set({ proposition_valeur: e.target.value })}
              placeholder="Une clôture de caisse en deux minutes, zéro erreur de TVA" className={`${FIELD} mt-1`} />
          </label>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <label className={LABEL}>Ce qui nous distingue
              <input value={f.differenciateurs} onChange={(e) => set({ differenciateurs: e.target.value })}
                placeholder="Installée en une matinée, support local" className={`${FIELD} mt-1`} />
            </label>
            <label className={LABEL}>Clients visés
              <input value={f.cible} onChange={(e) => set({ cible: e.target.value })}
                placeholder="Restaurants indépendants, 1 à 3 établissements" className={`${FIELD} mt-1`} />
            </label>
          </div>
          <label className={LABEL}>Tarif indicatif <span className="font-normal text-slate-400">(optionnel)</span>
            <input value={f.prix} onChange={(e) => set({ prix: e.target.value })}
              placeholder="À partir de 49 € HT / mois" className={`${FIELD} mt-1`} />
          </label>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <ListEditor title="Arguments" hint="Les bénéfices concrets, un par ligne." testid="solution-argument"
              items={f.arguments} onAdd={() => addItem("arguments")}
              onChange={(i, v) => setItem("arguments", i, v)} onRemove={(i) => removeItem("arguments", i)}
              placeholder="Menus du jour modifiés en un clic" />
            <ListEditor title="Preuves & références" hint="Chiffres et clients cités au téléphone." testid="solution-preuve"
              items={f.preuves} onAdd={() => addItem("preuves")}
              onChange={(i, v) => setItem("preuves", i, v)} onRemove={(i) => removeItem("preuves", i)}
              placeholder="40 restaurants équipés à Lille" />
          </div>
        </div>

        <div className="space-y-3">
          <ActivityPicker value={{ naf_prefixes: f.naf_prefixes, domaines: f.domaines }}
            onChange={(next) => set(next)} />
          <button type="button" data-testid="solution-active" onClick={() => set({ active: !f.active })}
            className="flex items-center gap-2.5 text-[13.5px] text-slate-600">
            <span className={`flex h-[24px] w-[42px] rounded-full p-[3px] ${f.active ? "justify-end bg-indigo-600" : "justify-start bg-slate-300"}`}>
              <span className="h-[18px] w-[18px] rounded-full bg-white shadow" />
            </span>
            {f.active ? "Proposée aux commerciaux" : "Retirée du catalogue (conservée)"}
          </button>
        </div>
      </div>

      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-600 hover:border-slate-300">Annuler</button>
        <button data-testid="solution-save" onClick={save} disabled={saving}
          className="flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60">
          {saving ? <RefreshCw size={15} className="animate-spin" /> : <Save size={15} />}
          {isNew ? "Ajouter au catalogue" : "Enregistrer"}
        </button>
      </div>
    </Modal>
  );
}

/** L'IA décrit la solution ; l'administrateur relit, puis applique dans le formulaire. */
function AssistPanel({ current, onApply, onClose }) {
  const [instruction, setInstruction] = useState("");
  const [loading, setLoading] = useState(false);
  const [proposal, setProposal] = useState(null);

  const ask = async () => {
    if (!instruction.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/solutions/assist", { instruction, current });
      setProposal(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Assistant indisponible"); }
    finally { setLoading(false); }
  };

  const rows = proposal ? [
    ["Type", proposal.type], ["Ce que c'est", proposal.description], ["Quand la proposer", proposal.contexte],
    ["Promesse", proposal.proposition_valeur], ["Ce qui nous distingue", proposal.differenciateurs],
    ["Clients visés", proposal.cible], ["Tarif", proposal.prix],
    ["Arguments", (proposal.arguments || []).join(" · ")], ["Preuves", (proposal.preuves || []).join(" · ")],
    ["Activités", (proposal.activites_labels || []).join(", ") || "toutes activités"],
  ].filter(([, v]) => v) : [];

  return (
    <div data-testid="solution-assist" className="mt-4 rounded-xl border border-indigo-100 bg-indigo-50/40 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[13.5px] font-semibold text-slate-900">Décrire la solution avec l'IA</div>
          <p className="mt-0.5 text-[12.5px] text-slate-500">
            Une phrase suffit — ce que c'est, pour qui. L'IA propose la fiche et les activités visées ;
            vous relisez avant d'enregistrer. Elle n'invente ni tarif ni référence.
          </p>
        </div>
        <button type="button" onClick={onClose} aria-label="Fermer l'assistant" className="text-slate-400 hover:text-slate-900"><X size={16} /></button>
      </div>
      <textarea data-testid="solution-assist-instruction" rows={2} value={instruction}
        onChange={(e) => setInstruction(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) ask(); }}
        placeholder="Ex. une caisse tactile connectée au stock, pour les restaurants et bars indépendants"
        className={`${FIELD} mt-3 resize-y bg-white`} />
      <button type="button" data-testid="solution-assist-run" onClick={ask} disabled={loading || !instruction.trim()}
        className="mt-2 flex items-center gap-2 rounded-xl bg-indigo-600 px-3.5 py-2 text-[13px] font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
        {loading ? <RefreshCw size={14} className="animate-spin" /> : <Wand2 size={14} />}
        {proposal ? "Proposer autre chose" : "Proposer une fiche"}
      </button>

      {proposal && (
        <div data-testid="solution-assist-proposal" className="mt-3 rounded-xl border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-3.5 py-2.5 font-heading text-[14px] font-bold text-slate-900">
            {proposal.nom || "Proposition"}
          </div>
          <dl className="grid grid-cols-[130px_minmax(0,1fr)] gap-x-3 gap-y-1.5 px-3.5 py-3 text-[12.5px]">
            {rows.map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="text-slate-400">{label}</dt>
                <dd className="text-slate-700 text-pretty">{value}</dd>
              </div>
            ))}
          </dl>
          <div className="flex gap-2 border-t border-slate-100 px-3.5 py-2.5">
            <button type="button" data-testid="solution-assist-apply" onClick={() => onApply(proposal)}
              className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-[12.5px] font-medium text-white hover:bg-slate-800">
              <Check size={13} /> Appliquer dans le formulaire
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ListEditor({ title, hint, items, onAdd, onChange, onRemove, placeholder, testid }) {
  return (
    <div>
      <div className="text-[12.5px] font-medium text-slate-600">{title}</div>
      <p className="mb-1.5 mt-0.5 text-[11.5px] text-slate-400">{hint}</p>
      <div className="space-y-1.5">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <input data-testid={testid} value={item} placeholder={placeholder}
              onChange={(e) => onChange(i, e.target.value)} className={FIELD} />
            <button type="button" onClick={() => onRemove(i)} title="Retirer"
              className="shrink-0 rounded-lg border border-slate-200 p-2 text-slate-400 hover:border-rose-200 hover:text-rose-600">
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={onAdd} data-testid={`${testid}-add`}
        className="mt-1.5 flex items-center gap-1 text-[12.5px] font-medium text-indigo-600 hover:text-indigo-800">
        <Plus size={13} /> Ajouter
      </button>
    </div>
  );
}
